import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/useAuth';
import ProtectedRoute from './auth/ProtectedRoute';
import RoleGuard from './auth/RoleGuard';
import LoginPage from './components/LoginPage';
import APShell from './components/APShell';
import AdminShell from './components/AdminShell';
import { Button, Card, Spinner, ToastProvider } from './components/ui';
import styles from './App.module.css';
import './App.css';

/**
 * Role-based redirect at the root path.
 * Sends Admin/CareManager to /admin, AP to /ap.
 */
function RoleRouter() {
  const { roles, isLoading, logout } = useAuth();

  if (isLoading) {
    return (
      <div className={styles.loadingCenter}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (roles.includes('Admin') || roles.includes('CareManager')) {
    return <Navigate to="/admin" replace />;
  }

  if (roles.includes('AP')) {
    return <Navigate to="/ap" replace />;
  }

  // Authenticated but no roles assigned
  return (
    <div className={styles.noRolePage}>
      <Card variant="elevated" padding="lg">
        <h1 className={styles.noRoleHeading}>No Role Assigned</h1>
        <p className={styles.noRoleText}>
          Your account does not have an application role. Contact your administrator.
        </p>
        <Button
          variant="ghost"
          onClick={() => void logout()}
        >
          Sign out
        </Button>
      </Card>
    </div>
  );
}

/**
 * Login route — redirects to home if already authenticated.
 */
function LoginRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className={styles.loadingCenter}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <LoginPage />;
}

function App() {
  return (
    <ToastProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <RoleRouter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ap/*"
          element={
            <ProtectedRoute>
              <RoleGuard allowedRoles={['AP', 'Admin']}>
                <APShell />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <RoleGuard allowedRoles={['CareManager', 'Admin']}>
                <AdminShell />
              </RoleGuard>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
